import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { subscribeToTaskMessages, createTaskMessage, deleteTaskMessage, updateTaskMessage, addMessageReaction, getAllUsers, createNotification } from '../services/firebaseService';

const TaskMessaging = ({ task, activity, project }) => {
  const { currentUser } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [showUserSuggestions, setShowUserSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editText, setEditText] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [showReactions, setShowReactions] = useState({});
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!task?.id) return;

    const unsubscribe = subscribeToTaskMessages(task.id, (messagesData) => {
      setMessages(messagesData);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [task?.id]);

  useEffect(() => {
    // Fetch users for mentions
    const fetchUsers = async () => {
      try {
        const usersData = await getAllUsers();
        setUsers(usersData);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    const position = e.target.selectionStart;
    
    setNewMessage(value);
    setCursorPosition(position);

    // Check for @ mentions - allow spaces and more characters
    const beforeCursor = value.substring(0, position);
    const mentionMatch = beforeCursor.match(/@([^@\n]*)$/);
    
    if (mentionMatch) {
      setMentionQuery(mentionMatch[1]);
      setShowUserSuggestions(true);
    } else {
      setShowUserSuggestions(false);
      setMentionQuery('');
    }
  };

  const insertMention = (user) => {
    const beforeCursor = newMessage.substring(0, newMessage.lastIndexOf('@'));
    const afterCursor = newMessage.substring(cursorPosition);
    
    // Use quotes around display names with spaces to make them easier to parse
    const mentionText = user.displayName.includes(' ') ? `"${user.displayName}"` : user.displayName;
    setNewMessage(`${beforeCursor}@${mentionText} ${afterCursor}`);
    setShowUserSuggestions(false);
    setMentionQuery('');
    
    // Focus back to textarea
    setTimeout(() => {
      textareaRef.current?.focus();
      const newPosition = beforeCursor.length + mentionText.length + 2;
      textareaRef.current?.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const extractMentions = (message) => {
    console.log('Extracting mentions from message:', message);
    console.log('Available users for mention matching:', users);
    
    // Enhanced regex patterns to handle different mention formats
    const patterns = [
      // Pattern 1: @"Full Name With Spaces" (quoted names)
      /@"([^"]+)"/g,
      // Pattern 2: @FullNameWithoutSpaces or @email
      /@([^\s@"]+)/g,
      // Pattern 3: @Full Name (unquoted multi-word names until space or end)
      /@([A-Za-z]+(?:\s+[A-Za-z]+)*?)(?=\s(?!@)|$|[.!?])/g
    ];
    
    const mentions = [];
    
    patterns.forEach(regex => {
      let match;
      while ((match = regex.exec(message)) !== null) {
        const mentionText = match[1].trim();
        console.log('Found mention pattern:', mentionText);
        
        const mentionedUser = users.find(user => {
          const mentionTextLower = mentionText.toLowerCase();
          const displayName = user.displayName?.toLowerCase() || '';
          const email = user.email?.toLowerCase() || '';
          
          // Check exact match, partial match, or email prefix
          return displayName === mentionTextLower || 
                 email === mentionTextLower ||
                 displayName.includes(mentionTextLower) ||
                 email.startsWith(mentionTextLower) ||
                 mentionTextLower.includes(displayName) ||
                 mentionTextLower.includes(email.split('@')[0]);
        });
        
        console.log('Matched user for mention:', mentionedUser);
        if (mentionedUser && !mentions.find(m => m.id === mentionedUser.id)) {
          mentions.push(mentionedUser);
        }
      }
    });
    
    console.log('Final extracted mentions:', mentions);
    return mentions;
  };

  const createMentionNotifications = async (mentions, messageText, messageId) => {
    console.log('Creating mention notifications for:', mentions);
    for (const mentionedUser of mentions) {
      try {
        console.log('Creating notification for mentioned user:', mentionedUser);
        const notificationData = {
          userId: mentionedUser.id,
          type: 'message_mention',
          title: 'You were mentioned in a message',
          message: `${currentUser.displayName || currentUser.email} mentioned you: "${messageText.substring(0, 100)}${messageText.length > 100 ? '...' : ''}"`,
          taskId: task.id,
          taskName: task.name,
          activityId: activity.id,
          activityName: activity.particulars,
          projectId: project.id,
          projectName: project.name,
          messageId: messageId,
          mentionedBy: currentUser.displayName || currentUser.email,
          mentionedByPhotoURL: currentUser.photoURL
        };
        console.log('Notification data for mention:', notificationData);
        await createNotification(notificationData);
        console.log('Mention notification created successfully');
      } catch (error) {
        console.error('Error creating mention notification:', error);
      }
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !currentUser) return;

    try {
      console.log('Sending message:', newMessage.trim());
      console.log('Current user:', currentUser);
      console.log('Task:', task);
      console.log('Activity:', activity);
      console.log('Project:', project);

      const messageData = {
        taskId: task.id,
        userId: currentUser.uid,
        userDisplayName: currentUser.displayName || currentUser.email,
        userEmail: currentUser.email,
        message: newMessage.trim(),
        mentions: extractMentions(newMessage).map(user => ({
          userId: user.id,
          displayName: user.displayName,
          email: user.email
        })),
        // Add reply information if replying to a message
        ...(replyingTo && {
          replyTo: {
            messageId: replyingTo.id,
            userId: replyingTo.userId,
            userDisplayName: replyingTo.userDisplayName,
            userEmail: replyingTo.userEmail,
            message: replyingTo.message,
            createdAt: replyingTo.createdAt
          }
        })
      };

      console.log('Message data to be created:', messageData);
      const messageId = await createTaskMessage(messageData);
      console.log('Message created with ID:', messageId);
      
      // Create notifications for mentioned users
      const mentions = extractMentions(newMessage);
      console.log('Extracted mentions for notifications:', mentions);
      if (mentions.length > 0) {
        console.log('Creating notifications for mentions...');
        await createMentionNotifications(mentions, newMessage.trim(), messageId);
        console.log('All mention notifications created');
      } else {
        console.log('No mentions found, skipping notification creation');
      }

      setNewMessage('');
      setReplyingTo(null);
      setShowUserSuggestions(false);
    } catch (error) {
      console.error('Error sending message:', error);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      await deleteTaskMessage(messageId);
    } catch (error) {
      console.error('Error deleting message:', error);
    }
  };

  const handleEditMessage = (message) => {
    setEditingMessage(message.id);
    setEditText(message.message);
  };

  const handleSaveEdit = async () => {
    if (!editText.trim() || !editingMessage) return;
    
    try {
      await updateTaskMessage(editingMessage, { 
        message: editText.trim(),
        mentions: extractMentions(editText).map(user => ({
          userId: user.id,
          displayName: user.displayName,
          email: user.email
        }))
      });
      setEditingMessage(null);
      setEditText('');
    } catch (error) {
      console.error('Error updating message:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setEditText('');
  };

  const handleReaction = async (messageId, reaction) => {
    try {
      await addMessageReaction(messageId, currentUser.uid, reaction);
    } catch (error) {
      console.error('Error adding reaction:', error);
    }
  };

  const handleReply = (message) => {
    setReplyingTo(message);
    // Use quotes for display names with spaces
    const mentionText = (message.userDisplayName || message.userEmail).includes(' ') 
      ? `"${message.userDisplayName || message.userEmail}"` 
      : message.userDisplayName || message.userEmail;
    setNewMessage(`@${mentionText} `);
    textareaRef.current?.focus();
  };

  const toggleReactions = (messageId) => {
    setShowReactions(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const formatMessage = (message) => {
    let formattedMessage = message;
    
    // Enhanced regex patterns to handle different mention formats
    const patterns = [
      // Pattern 1: @"Full Name With Spaces" (quoted names) - remove quotes in display
      { 
        regex: /@"([^"]+)"/g, 
        replacement: '<span class="bg-blue-100 text-blue-800 px-1 rounded font-medium">@$1</span>' 
      },
      // Pattern 2: @FullNameWithoutSpaces or @email
      { 
        regex: /@([^\s@"]+)/g, 
        replacement: '<span class="bg-blue-100 text-blue-800 px-1 rounded font-medium">@$1</span>' 
      },
      // Pattern 3: @Full Name (unquoted multi-word names)
      { 
        regex: /@([A-Za-z]+(?:\s+[A-Za-z]+)*?)(?=\s(?!@)|$|[.!?])/g, 
        replacement: '<span class="bg-blue-100 text-blue-800 px-1 rounded font-medium">@$1</span>' 
      }
    ];
    
    // Apply each pattern to format mentions
    patterns.forEach(({ regex, replacement }) => {
      formattedMessage = formattedMessage.replace(regex, replacement);
    });
    
    return formattedMessage;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const filteredUsers = users.filter(user => 
    user.displayName?.toLowerCase().includes(mentionQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(mentionQuery.toLowerCase())
  ).slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="text-gray-500">Loading messages...</div>
      </div>
    );
  }

  // Get unique collaborators from messages
  const getCollaborators = () => {
    const collaboratorsMap = new Map();
    
    // Add current user
    collaboratorsMap.set(currentUser.uid, {
      id: currentUser.uid,
      displayName: currentUser.displayName,
      email: currentUser.email,
      photoURL: currentUser.photoURL
    });
    
    // Add users from messages
    messages.forEach(message => {
      if (!collaboratorsMap.has(message.userId)) {
        collaboratorsMap.set(message.userId, {
          id: message.userId,
          displayName: message.userDisplayName,
          email: message.userEmail,
          photoURL: message.userPhotoURL
        });
      }
    });
    
    return Array.from(collaboratorsMap.values());
  };

  const collaborators = getCollaborators();

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Collaborators Avatar Stack - Compact Header */}
      <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">Collaborators:</span>
          <div className="flex -space-x-1">
            {collaborators.slice(0, 4).map((collaborator, index) => (
              <div
                key={collaborator.id}
                className="relative"
                style={{ zIndex: collaborators.length - index }}
                title={collaborator.displayName || collaborator.email}
              >
                <img
                  src={collaborator.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(collaborator.displayName || collaborator.email || 'User')}&background=3b82f6&color=ffffff`}
                  alt={collaborator.displayName || collaborator.email}
                  className="h-6 w-6 rounded-full border-2 border-white"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(collaborator.displayName || collaborator.email || 'User')}&background=3b82f6&color=ffffff`;
                  }}
                />
              </div>
            ))}
            {collaborators.length > 4 && (
              <div className="h-6 w-6 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center">
                <span className="text-xs text-gray-600 font-medium">+{collaborators.length - 4}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <p className="mt-2">No messages yet. Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className="flex space-x-3 group hover:bg-gray-50 p-2 rounded">
              <div className="flex-shrink-0">
                <img
                  src={message.userPhotoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(message.userDisplayName || message.userEmail || 'User')}&background=3b82f6&color=ffffff`}
                  alt={message.userDisplayName || message.userEmail || 'User'}
                  className="h-8 w-8 rounded-full"
                  onError={(e) => {
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(message.userDisplayName || message.userEmail || 'User')}&background=3b82f6&color=ffffff`;
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <p className="text-sm font-medium text-gray-900">
                    {message.userDisplayName || message.userEmail}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatDate(message.createdAt)}
                    {message.isEdited && <span className="ml-1 text-xs text-gray-400">(edited)</span>}
                  </p>
                  
                  {/* Message Actions - Only show on hover */}
                  <div className="opacity-0 group-hover:opacity-100 flex items-center space-x-1 transition-opacity">
                    {/* Reply Button */}
                    <button
                      onClick={() => handleReply(message)}
                      className="text-gray-400 hover:text-blue-600 text-xs p-1 rounded"
                      title="Reply"
                    >
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                      </svg>
                    </button>
                    
                    {/* Reactions Button */}
                    <button
                      onClick={() => toggleReactions(message.id)}
                      className="text-gray-400 hover:text-yellow-600 text-xs p-1 rounded"
                      title="Add reaction"
                    >
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    
                    {/* Edit/Delete for message owner */}
                    {message.userId === currentUser.uid && (
                      <>
                        <button
                          onClick={() => handleEditMessage(message)}
                          className="text-gray-400 hover:text-green-600 text-xs p-1 rounded"
                          title="Edit message"
                        >
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDeleteMessage(message.id)}
                          className="text-gray-400 hover:text-red-600 text-xs p-1 rounded"
                          title="Delete message"
                        >
                          <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
                
                {/* Reply Context */}
                {message.replyTo && (
                  <div className="mt-2 mb-2 p-2 bg-gray-50 border-l-4 border-gray-300 rounded">
                    <div className="text-xs text-gray-600">
                      <span className="font-medium">{message.replyTo.userDisplayName || message.replyTo.userEmail}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 truncate">
                      {message.replyTo.message.length > 80 ? `${message.replyTo.message.substring(0, 80)}...` : message.replyTo.message}
                    </div>
                  </div>
                )}
                
                {/* Message Content or Edit Form */}
                {editingMessage === message.id ? (
                  <div className="mt-2 space-y-2">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="w-full p-2 border border-gray-300 rounded-md resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      rows={2}
                      placeholder="Edit your message..."
                    />
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveEdit}
                        className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                      >
                        Save
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 bg-gray-300 text-gray-700 text-xs rounded hover:bg-gray-400"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div 
                    className="mt-1 text-sm text-gray-700"
                    dangerouslySetInnerHTML={{ __html: formatMessage(message.message) }}
                  />
                )}
                
                {/* Reactions Display */}
                {message.reactions && Object.keys(message.reactions).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {Object.entries(message.reactions).map(([reaction, users]) => (
                      <button
                        key={reaction}
                        onClick={() => handleReaction(message.id, reaction)}
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs ${
                          users.includes(currentUser.uid)
                            ? 'bg-blue-100 text-blue-800 border border-blue-300'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        title={`${users.length} reaction${users.length !== 1 ? 's' : ''}`}
                      >
                        <span className="mr-1">{reaction}</span>
                        <span>{users.length}</span>
                      </button>
                    ))}
                  </div>
                )}
                
                {/* Reaction Picker */}
                {showReactions[message.id] && (
                  <div className="mt-2 flex space-x-1 p-2 bg-gray-50 rounded">
                    {['👍', '❤️', '😊', '😂', '😮', '😢', '😡'].map(emoji => (
                      <button
                        key={emoji}
                        onClick={() => {
                          handleReaction(message.id, emoji);
                          toggleReactions(message.id);
                        }}
                        className="text-lg hover:bg-gray-200 p-1 rounded transition-colors"
                        title={`React with ${emoji}`}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="border-t border-gray-200 p-3">
        {/* Reply indicator */}
        {replyingTo && (
          <div className="mb-2 p-2 bg-blue-50 border-l-4 border-blue-400 rounded">
            <div className="flex items-center justify-between">
              <div className="text-sm text-blue-800">
                <span className="font-medium">Replying to {replyingTo.userDisplayName || replyingTo.userEmail}</span>
                <p className="text-xs text-blue-600 mt-1 truncate">
                  {replyingTo.message.substring(0, 100)}...
                </p>
              </div>
              <button
                onClick={() => setReplyingTo(null)}
                className="text-blue-400 hover:text-blue-600"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}
        
        <form onSubmit={handleSendMessage} className="relative">
          <div className="relative">
            <textarea
              ref={textareaRef}
              value={newMessage}
              onChange={handleInputChange}
              placeholder="Type a message... Use @username to mention someone"
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows="3"
            />
            
            {/* User Suggestions Dropdown */}
            {showUserSuggestions && filteredUsers.length > 0 && (
              <div className="absolute bottom-full left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg mb-1 max-h-32 overflow-y-auto z-10">
                {filteredUsers.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => insertMention(user)}
                    className="w-full text-left px-3 py-2 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <img
                      src={user.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email)}&background=3b82f6&color=ffffff`}
                      alt={user.displayName || user.email}
                      className="h-6 w-6 rounded-full"
                      onError={(e) => {
                        e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.displayName || user.email)}&background=3b82f6&color=ffffff`;
                      }}
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{user.displayName}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          <div className="mt-3 flex justify-between items-center">
            <p className="text-xs text-gray-500">
              Use @username to mention team members
            </p>
            <button
              type="submit"
              disabled={!newMessage.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TaskMessaging;
